import React from 'react'
import { useAuth } from '../context/AuthContext'
import { recurringTaskService } from '../services/recurringTaskService'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Textarea } from '../components/ui/textarea'
import {
  ClipboardCheck,
  Download,
  CheckCircle,
  XCircle,
  FileText,
  ChevronDown,
  ChevronUp,
  Filter,
} from 'lucide-react'
import { ApprovalItem, APPROVAL_STATUSES } from '../types/recurringTasks'
import { getTaskType } from '../features/recurringTasks/taskTypeRegistry'

export default function ApprovalsView() {
  const { partner, managedCustomers } = useAuth()
  const partnerId = partner?.partnerId || ''

  const [approvals, setApprovals] = React.useState<ApprovalItem[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)

  // Filters
  const [statusFilter, setStatusFilter] = React.useState<string>('')
  const [customerFilter, setCustomerFilter] = React.useState<string>('')

  // Expanded rows
  const [expandedId, setExpandedId] = React.useState<string | null>(null)

  // Reject dialog
  const [rejectingId, setRejectingId] = React.useState<string | null>(null)
  const [rejectReason, setRejectReason] = React.useState('')
  const [rejectError, setRejectError] = React.useState<string | null>(null)
  const [isProcessing, setIsProcessing] = React.useState(false)

  // Per-task-type approval action dispatch
  const [approvingApproval, setApprovingApproval] = React.useState<ApprovalItem | null>(null)

  const loadApprovals = React.useCallback(async () => {
    if (!partnerId) return
    setIsLoading(true)
    setError(null)

    try {
      const params: { status?: string; customerId?: string } = {}
      if (statusFilter) params.status = statusFilter
      if (customerFilter) params.customerId = customerFilter

      const res = await recurringTaskService.getApprovals(partnerId, params)
      if (res.success && res.data) {
        setApprovals(res.data)
      }
    } catch (err) {
      setError('Failed to load approvals')
    } finally {
      setIsLoading(false)
    }
  }, [partnerId, statusFilter, customerFilter])

  React.useEffect(() => {
    loadApprovals()
  }, [loadApprovals])

  // Legacy direct-approve fallback used when there is no registered
  // ApprovalAction component for the approval's task type. New task types
  // should always register an action so we can run validation before saving.
  const handleApproveDirect = async (approvalId: string) => {
    setIsProcessing(true)
    setError(null)
    try {
      const res = await recurringTaskService.approveItem(partnerId, approvalId)
      if (res.success) {
        setSuccessMessage('Approval updated successfully')
        setTimeout(() => setSuccessMessage(null), 3000)
        await loadApprovals()
      } else {
        setError(res.error || 'Failed to approve')
      }
    } catch (err) {
      setError('Failed to approve item')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleApproveClick = (approval: ApprovalItem) => {
    const def = getTaskType(approval.taskType)
    if (def) {
      // Hand off to the per-type action component (renders its own modal).
      setApprovingApproval(approval)
    } else {
      // Unknown / unregistered task type — fall back to a plain server-side
      // approve so partial deployments don't break the queue.
      handleApproveDirect(approval.id)
    }
  }

  const handleApproveActionComplete = async () => {
    setApprovingApproval(null)
    setSuccessMessage('Approval updated successfully')
    setTimeout(() => setSuccessMessage(null), 3000)
    await loadApprovals()
  }

  const handleReject = async () => {
    if (!rejectingId || isProcessing) return
    setIsProcessing(true)
    setRejectError(null)
    try {
      const res = await recurringTaskService.rejectItem(partnerId, rejectingId, rejectReason)
      if (res.success) {
        setRejectingId(null)
        setRejectReason('')
        setSuccessMessage('Report rejected')
        setTimeout(() => setSuccessMessage(null), 3000)
        await loadApprovals()
      } else {
        setRejectError(res.error || 'Failed to reject report. Please try again.')
      }
    } catch (err: any) {
      setRejectError(err?.message || 'Failed to reject report. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDownload = async (approvalId: string, attachmentId: string) => {
    try {
      const res = await recurringTaskService.getAttachmentDownloadUrl(partnerId, approvalId, attachmentId)
      if (res.success && res.data) {
        window.open(res.data.url, '_blank')
      } else {
        setError('Failed to get download link')
      }
    } catch (err) {
      setError('Failed to download attachment')
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PendingApproval':
        return <Badge className="bg-amber-100 text-amber-800">Pending Approval</Badge>
      case 'Approved':
        return <Badge variant="success">Approved</Badge>
      case 'AutoApproved':
        return <Badge variant="success">Auto-approved</Badge>
      case 'CompletedManually':
        return <Badge variant="success">Completed manually</Badge>
      case 'Rejected':
        return <Badge variant="destructive">Rejected</Badge>
      default:
        return <Badge variant="default">{status}</Badge>
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  const pendingCount = approvals.filter(a => a.status === 'PendingApproval').length

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-[1400px] mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Approvals</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review and approve reports from recurring tasks
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                {pendingCount} pending
              </span>
            )}
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-4 bg-white rounded-lg border border-slate-200 p-4">
          <Filter className="h-4 w-4 text-slate-400" />
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Customer</label>
            <select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Customers</option>
              {managedCustomers.map((c) => (
                <option key={c.customerId} value={c.customerId}>
                  {c.customerName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Statuses</option>
              {APPROVAL_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 rounded-full border-4 border-slate-200 border-t-orange-600 animate-spin" />
          </div>
        ) : approvals.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No approvals</h3>
            <p className="text-sm text-slate-500 mt-1">
              {statusFilter || customerFilter
                ? 'No approvals match the current filters'
                : 'Reports from recurring tasks will appear here for review'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {approvals.map((approval) => (
              <div key={approval.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                {/* Row header */}
                <button
                  type="button"
                  onClick={() => setExpandedId(expandedId === approval.id ? null : approval.id)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <FileText className="h-5 w-5 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{approval.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {approval.customerName} &middot;{' '}
                        {new Date(approval.createdAt).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {getStatusBadge(approval.status)}
                    {approval.attachments.length > 0 && (
                      <span className="text-xs text-slate-500">
                        {approval.attachments.length} file{approval.attachments.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {expandedId === approval.id ? (
                      <ChevronUp className="h-4 w-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Expanded detail */}
                {expandedId === approval.id && (
                  <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
                    {/* Attachments */}
                    {approval.attachments.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2">Attachments</h4>
                        <div className="space-y-2">
                          {approval.attachments.map((att) => (
                            <div
                              key={att.id}
                              className="flex items-center justify-between bg-white rounded-md border border-slate-200 px-4 py-2.5"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-slate-900 truncate">{att.fileName}</div>
                                  <div className="text-xs text-slate-500">{formatFileSize(att.fileSize)}</div>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownload(approval.id, att.id)}
                              >
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                Download
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rejection reason */}
                    {approval.status === 'PendingApproval' && approval.validationMessage && (
                      <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
                        {approval.validationMessage}
                      </div>
                    )}
                    {approval.status === 'Rejected' && approval.rejectedReason && (
                      <div className="mb-4 rounded-md bg-rose-50 border border-rose-200 px-4 py-3">
                        <div className="text-xs font-medium text-rose-800 mb-1">Rejection Reason</div>
                        <div className="text-sm text-rose-700">{approval.rejectedReason}</div>
                      </div>
                    )}

                    {/* Approval info */}
                    {['Approved', 'AutoApproved', 'CompletedManually'].includes(approval.status) && approval.approvedAt && (
                      <div className="mb-4 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3">
                        <div className="text-sm text-emerald-700">
                          {approval.status === 'CompletedManually' ? 'Marked completed manually on ' : 'Approved on '}
                          {new Date(approval.approvedAt).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                          {approval.approvedByUserId && ` by ${approval.approvedByUserId}`}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {approval.status === 'PendingApproval' && (
                      <div className="flex items-center gap-3 pt-2">
                        <Button
                          size="sm"
                          onClick={() => handleApproveClick(approval)}
                          loading={isProcessing}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {approval.taskType === 'BlitzReport' ? 'Review report' : 'Approve'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRejectingId(approval.id)
                            setRejectReason('')
                            setRejectError(null)
                          }}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-task-type approval action (e.g. BlitzReportApprovalAction) */}
      {approvingApproval && (() => {
        const def = getTaskType(approvingApproval.taskType)
        if (!def) return null
        const ActionComponent = def.ApprovalAction
        return (
          <ActionComponent
            approval={approvingApproval}
            attachments={approvingApproval.attachments}
            onComplete={handleApproveActionComplete}
            onCancel={() => setApprovingApproval(null)}
          />
        )
      })()}

      {/* Reject Dialog */}
      <Dialog open={rejectingId !== null} onOpenChange={(open) => { if (!open && !isProcessing) setRejectingId(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Report</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this report. This will be visible in the approval history.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {rejectError && (
              <div role="alert" className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {rejectError}
              </div>
            )}
            <Textarea
              value={rejectReason}
              disabled={isProcessing}
              maxLength={500}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" disabled={isProcessing} onClick={() => setRejectingId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} loading={isProcessing}>
              Reject Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
